import type { Produto } from 'config/produto-env';
import type { ControlDate } from 'types/controle-datas';

/**
 * Cronograma da home a partir da `controle_datas__c` (spec datas-unificadas-2027, DATA-02).
 * Função pura: as homes só passam as datas já carregadas pelo AuthContext.
 */

export type StatusEtapa = 'concluido' | 'em_andamento' | 'pendente';

export interface EtapaCronograma {
  /** Posição na linha do tempo, começando em 1. */
  id: number;
  _id: string;
  titulo: string;
  descricao: string;
  /** Datas em horário de Brasília: `dd/mm`, ou `dd/mm/aaaa` quando o período cruza o ano. */
  dataInicio: string;
  /** Vazio quando a etapa não tem fim. */
  dataFim: string;
  status: StatusEtapa;
  percentual?: number;
  badge?: string;
}

const CATEGORIA_POR_PRODUTO: Record<Produto, string> = {
  PREMIO_BB: 'conexao',
  PREMIO_ELBB: 'elbb',
};

const FUSO_BRASILIA = 'America/Sao_Paulo';
const SEM_ORDEM = Number.MAX_SAFE_INTEGER;

/** Mesma regra do `filterByCategory`: a categoria do produto ou "todas". */
function pertenceAoProduto(data: ControlDate, produto: Produto): boolean {
  const categorias = Array.isArray(data.categoria) ? data.categoria : [data.categoria];
  return categorias.includes(CATEGORIA_POR_PRODUTO[produto]) || categorias.includes('todas');
}

function paraData(valor: ControlDate['dataInicio'] | null | undefined): Date | null {
  if (!valor) return null;
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? null : data;
}

function anoEmBrasilia(data: Date): string {
  return data.toLocaleDateString('pt-BR', { timeZone: FUSO_BRASILIA, year: 'numeric' });
}

function formatarData(data: Date, comAno: boolean): string {
  return data.toLocaleDateString('pt-BR', {
    timeZone: FUSO_BRASILIA,
    day: '2-digit',
    month: '2-digit',
    ...(comAno ? { year: 'numeric' } : {}),
  });
}

function calcularStatus(inicio: Date, fim: Date | null, agora: Date): StatusEtapa {
  if (fim && agora > fim) return 'concluido';
  if (agora >= inicio) return 'em_andamento';
  return 'pendente';
}

function calcularPercentual(inicio: Date, fim: Date | null, agora: Date): number | undefined {
  if (!fim) return undefined;
  const total = fim.getTime() - inicio.getTime();
  if (total <= 0) return 100;
  return Math.min(Math.round(((agora.getTime() - inicio.getTime()) / total) * 100), 100);
}

export interface PeriodoFase {
  inicio: Date;
  fim: Date;
}

/** Início e fim de uma fase; `null` quando falta alguma das datas ou ela é inválida. */
export function getPeriodoFase(controlDate: ControlDate | undefined): PeriodoFase | null {
  const inicio = paraData(controlDate?.dataInicio);
  const fim = paraData(controlDate?.dataFim);
  return inicio && fim ? { inicio, fim } : null;
}

/** `dd/mm` em horário de Brasília. */
export function formatarDiaMes(data: Date): string {
  return formatarData(data, false);
}

/** `dd/mm/aaaa` em horário de Brasília. */
export function formatarDiaMesAno(data: Date): string {
  return formatarData(data, true);
}

/**
 * `dd/mm a dd/mm` em horário de Brasília, ou `dd/mm/aaaa a dd/mm/aaaa` quando o
 * período cruza o ano; vazio sem período.
 */
export function formatarPeriodo(periodo: PeriodoFase | null): string {
  if (!periodo) return '';
  const comAno = anoEmBrasilia(periodo.inicio) !== anoEmBrasilia(periodo.fim);
  return `${formatarData(periodo.inicio, comAno)} a ${formatarData(periodo.fim, comAno)}`;
}

export function montarCronograma(
  controlDates: ControlDate[],
  produto: Produto,
  agora: Date = new Date()
): EtapaCronograma[] {
  const etapasValidas = controlDates
    .filter((data) => data.exibirNoCronograma && pertenceAoProduto(data, produto))
    .flatMap((data) => {
      const inicio = paraData(data.dataInicio);
      if (!inicio) {
        console.warn(`[montarCronograma] ${data._id} sem dataInicio válida; fora do cronograma.`);
        return [];
      }
      return [{ data, inicio, fim: paraData(data.dataFim) }];
    });

  return etapasValidas
    .sort(
      (a, b) =>
        (a.data.ordem ?? SEM_ORDEM) - (b.data.ordem ?? SEM_ORDEM) ||
        a.inicio.getTime() - b.inicio.getTime()
    )
    .map(({ data, inicio, fim }, index) => {
      const status = calcularStatus(inicio, fim, agora);
      const comAno = !!fim && anoEmBrasilia(inicio) !== anoEmBrasilia(fim);
      return {
        id: index + 1,
        _id: data._id,
        titulo: data.titulo,
        descricao: data.descricao ?? '',
        dataInicio: formatarData(inicio, comAno),
        dataFim: fim ? formatarData(fim, comAno) : '',
        status,
        percentual: status === 'em_andamento' ? calcularPercentual(inicio, fim, agora) : undefined,
        badge: status === 'em_andamento' ? 'Em andamento' : undefined,
      };
    });
}
